'use client';

import { Button, type ButtonProps } from '@/app/components/button';
import { Icon, type IconType } from '@/app/components/icons';
import { IconAdd } from '@/app/components/icons/ui-actions/icon-add';
import { IconClose } from '@/app/components/icons/ui-actions/icon-close';
import type { Theme } from '@/app/types/styles/theme';
import { cn } from '@/app/utils/cn';
import { cnva } from '@/app/utils/cnva';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';

type TagType = 'selecting' | 'adding';
export interface TagItem extends Omit<ButtonProps, 'onClick' | 'type'> {
  id: string;
  label: string;
  iconBefore?: IconType;
  iconAfter?: IconType;
  selected?: boolean;
}

export interface TagIconProps {
  type: TagType;
  selected: boolean;
  icon?: IconType;
  placement?: 'before' | 'after';
  noIcon?: boolean;
  iconStyled?: string;
}

export interface TagItemProps extends TagItem {
  type?: TagType;
  theme?: Theme;
  tagCustomStyled?: TagCustomStyle;
  iconCustomStyled?: TagCustomStyle;
  eventName?: string;
  index?: number;
  noIcon?: boolean;
  onClick?: () => void;
}
export interface TagCustomStyle {
  base?: string;
  selected?: string;
}

interface TagsProps {
  tags: TagItem[];
  onChange: (updatedTag: TagItem) => void;
  type?: TagType;
  theme?: Theme;
  className?: string;
  tagCustomStyled?: TagCustomStyle;
  iconCustomStyled?: TagCustomStyle;
  eventName?: string;
  noIcon?: boolean;
}

const TagStyled = cnva(
  'group transition-form font-noto h-8 w-max min-w-0 py-1',
  {
    variants: {
      theme: {
        light:
          'bg-white text-gray-900 hover:bg-gray-50 hover:text-gray-900 focus-visible:bg-gray-50 focus-visible:text-gray-900 active:bg-gray-50 active:text-gray-900',
        dark: 'bg-gray-900 text-white hover:bg-gray-700 hover:text-white focus-visible:bg-gray-700 focus-visible:text-white active:bg-gray-700 active:text-white',
      },
      selected: {
        true: 'font-noto-bold',
        false: 'before:border-1',
      },
    },
    compoundVariants: [
      {
        theme: 'light',
        selected: false,
        className:
          'before:border-gray-800 hover:before:border-gray-50 focus-visible:before:border-gray-50 active:before:border-gray-50',
      },
      {
        theme: 'light',
        selected: true,
        className:
          'bg-gray-800 text-white hover:bg-gray-800 hover:text-white focus-visible:bg-gray-800 focus-visible:text-white',
      },

      {
        theme: 'dark',
        selected: false,
        className:
          'before:border-white hover:before:border-gray-700 focus-visible:before:border-gray-700 active:before:border-gray-700',
      },
      {
        theme: 'dark',
        selected: true,
        className:
          'bg-white text-gray-900 hover:bg-white hover:text-gray-900 focus-visible:bg-white focus-visible:text-gray-900',
      },
    ],
    defaultVariants: {
      theme: 'light',
      selected: false,
    },
  }
);

const TagIconStyled = cnva('size-5 flex-none', {
  variants: {
    theme: {
      light: 'text-gray-800',
      dark: 'text-white',
    },
    selected: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      theme: 'light',
      selected: true,
      className:
        'text-white group-hover:text-white group-focus-visible:text-white',
    },
    {
      theme: 'dark',
      selected: true,
      className:
        'text-gray-900 group-hover:text-gray-900 group-focus-visible:text-gray-900',
    },
  ],
  defaultVariants: {
    theme: 'light',
    selected: false,
  },
});

const CustomTagStyled = ({ base, selected }: TagCustomStyle) =>
  cnva(base, {
    variants: {
      selected: {
        true: selected,
      },
    },
    defaultVariants: {
      selected: false,
    },
  });

const TagIcon = ({
  type,
  selected,
  icon,
  placement = 'before',
  noIcon = false,
  iconStyled,
}: TagIconProps) => {
  if (noIcon) return null;

  if (type === 'selecting' && icon) {
    return <Icon icon={icon} className={iconStyled} />;
  }

  if (type === 'adding') {
    if (placement === 'before' && !selected) {
      return <Icon icon={<IconAdd />} className={iconStyled} />;
    }
    if (placement === 'after' && selected) {
      return <Icon icon={<IconClose />} className={iconStyled} />;
    }
  }

  return null;
};

const TagItem = ({
  label,
  type = 'selecting',
  theme = 'light',
  index,
  eventName,
  iconBefore,
  iconAfter,
  selected = false,
  onClick,
  tagCustomStyled,
  iconCustomStyled,
  noIcon = false,
  ...props
}: TagItemProps) => {
  const tagStyled = TagStyled({
    theme,
    selected,
    className: tagCustomStyled
      ? CustomTagStyled(tagCustomStyled)({ selected })
      : undefined,
  });

  const iconStyled = TagIconStyled({
    theme: iconCustomStyled ? undefined : theme,
    selected,
    className: iconCustomStyled
      ? CustomTagStyled(iconCustomStyled)({ selected })
      : undefined,
  });

  return (
    <Button
      variant={selected ? 'primary' : 'secondary'}
      size="sm"
      onClick={onClick}
      className={tagStyled}
      {...createDataAttributes({
        test: eventName ? `${eventName}-${index}` : `tag-${index}`,
      })}
      {...createDataGA({
        click: true,
        eventName: eventName || 'tag',
        text: label,
        value: String(selected),
      })}
      {...props}
    >
      <TagIcon
        placement="before"
        type={type}
        selected={selected}
        icon={iconBefore}
        noIcon={noIcon}
        iconStyled={iconStyled}
      />
      <span className="truncate leading-6">{label}</span>
      <TagIcon
        placement="after"
        type={type}
        selected={selected}
        icon={iconAfter}
        noIcon={noIcon}
        iconStyled={iconStyled}
      />
    </Button>
  );
};

const Tags = ({
  type = 'selecting',
  tags = [],
  theme = 'light',
  onChange,
  className,
  tagCustomStyled,
  iconCustomStyled,
  eventName,
  noIcon = false,
}: TagsProps) => {
  return (
    <div
      className={cn('flex flex-wrap gap-4', className)}
      role="group"
      {...createDataAttributes({ test: 'tag-group' })}
    >
      {tags.map((tag, index) => (
        <TagItem
          key={`${tag.id}${index}`}
          id={tag.id}
          type={type}
          label={tag.label}
          theme={theme}
          iconBefore={tag.iconBefore}
          iconAfter={tag.iconAfter}
          selected={tag.selected}
          onClick={() => {
            onChange(tag);
          }}
          tagCustomStyled={tagCustomStyled}
          iconCustomStyled={iconCustomStyled}
          index={index}
          eventName={eventName}
          noIcon={noIcon}
        />
      ))}
    </div>
  );
};

export { TagItem, Tags };
